import { AuthUser, Session } from '../machine/types'

/**
 * Structural sharing for refreshed sessions (AOS-1026).
 *
 * A silent token refresh re-parses claims and would otherwise produce a
 * session whose `claims.roles` array and `claims.user` object are new
 * references even when their contents are identical. Every consumer keyed
 * on those references (React effect deps, reselect inputs, the Redux
 * session bridge) then treats the refresh as a change — with a 5-minute
 * token lifespan that cascade fires every ~3 minutes and resets in-flight
 * UI state.
 *
 * This helper reuses the previous references when contents are equal, so
 * an unchanged-claims refresh is a no-op for reference-keyed consumers,
 * while a real change (e.g. a Keycloak group update) still propagates.
 *
 * Deliberately per-field: `expiresAt` and the token strings ALWAYS come
 * from the fresh session — the proactive-refresh scheduler derives the
 * next refresh time from `expiresAt`, so it must advance on every
 * refresh. Never reuse the whole previous claims/session object.
 */
export const stabilizeRefreshedSession = (previous: Session, next: Session): Session => {
    const roles = stringArraysEqual(previous.claims.roles, next.claims.roles)
        ? previous.claims.roles
        : next.claims.roles
    const user = usersEqual(previous.claims.user, next.claims.user)
        ? previous.claims.user
        : next.claims.user
    // AOS-1165 introduced `claims.airports` (parsed fresh — a new array on
    // every refresh, including the empty one). Stabilize it like `roles`, or
    // the reference cascade this helper exists to stop comes right back
    // through `useAuth().airports` consumers (`useAuthInfo`, `useCanViewTasks`).
    const airports =
        previous.claims.airports !== undefined &&
        next.claims.airports !== undefined &&
        stringArraysEqual(previous.claims.airports, next.claims.airports)
            ? previous.claims.airports
            : next.claims.airports

    if (
        roles === next.claims.roles &&
        user === next.claims.user &&
        airports === next.claims.airports
    ) {
        return next
    }
    return { ...next, claims: { ...next.claims, roles, user, airports } }
}

/** Order-insensitive content equality — Keycloak gives no ordering promise. */
const stringArraysEqual = (a: readonly string[], b: readonly string[]): boolean => {
    if (a === b) {
        return true
    }
    if (a.length !== b.length) {
        return false
    }
    const set = new Set(a)
    return b.every(item => set.has(item))
}

/**
 * Shallow own-key equality. `AuthUser` is a flat claims projection; custom
 * `mapUser` implementations may add fields, so compare every own key from
 * both sides instead of a hard-coded list.
 */
const usersEqual = (a: AuthUser, b: AuthUser): boolean => {
    if (a === b) {
        return true
    }
    const aKeys = Object.keys(a) as (keyof AuthUser)[]
    const bKeys = Object.keys(b) as (keyof AuthUser)[]
    if (aKeys.length !== bKeys.length) {
        return false
    }
    return aKeys.every(key => a[key] === b[key])
}
